import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiCheck, FiArrowRight, FiMail } from 'react-icons/fi';
import { motion } from 'framer-motion';
import Header from '../components/Header';
import { AuthContext } from '../context/AuthContext';
import useApi from '../hooks/useApi';
import CustomPlanModal from '../components/pricing/CustomPlanModal';
import { API_URL } from '../config/constants';

// Use centralized API_URL
const API_BASE_URL = API_URL;

const PricingPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useContext(AuthContext);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isCustomPlanModalOpen, setIsCustomPlanModalOpen] = useState(false);
  const [pricingPlans, setPricingPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Get real data from API - only if authenticated
  const { data: apiData } = useApi('billing', { enabled: isAuthenticated });
  const [currentPlan, setCurrentPlan] = useState(null); // No default plan - only set when authenticated
  
  // Fetch plans from backend
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        // Use the correct API endpoint that matches the backend route
        const url = `${API_BASE_URL}/plans`;
        console.log('[PricingPage] Fetching plans from:', url);
        const response = await fetch(url);
        console.log('[PricingPage] Response status:', response.status);
        const data = await response.json();
        console.log('[PricingPage] Response data:', data);
        
        if (data.success && data.data && data.data.plans) {
          // Transform backend plans to frontend format
          const transformedPlans = data.data.plans.map((plan, index) => ({
            id: plan.id,
            name: plan.id === 'starter' ? 'Starter ( Free Trial Pack )' : plan.name,
            price: typeof plan.price === 'number' ? `₹${plan.price.toLocaleString('en-IN')}` : plan.price,
            priceValue: plan.price,
            priceId: `price_${plan.id}`,
            description: getDescription(plan.id),
            features: plan.features || [],
            popular: plan.id === 'pro',
            isFree: plan.price === 0}));
          
          setPricingPlans(transformedPlans);
        } else {
          // If the response structure is not as expected, use default plans
          console.warn('Unexpected plan data structure:', data);
          setPricingPlans(getDefaultPlans());
        }
      } catch (err) {
        console.error('[PricingPage] Error fetching plans:', err);
        console.error('[PricingPage] Error details:', {
          message: err.message,
          stack: err.stack,
          apiUrl: `${API_BASE_URL}/plans`
        });
        // Fallback to default plans if API fails
        setPricingPlans(getDefaultPlans());
      } finally {
        setLoading(false);
      }
    };
    
    fetchPlans();
  }, []);
  
  // Helper function to get plan descriptions
  const getDescription = (planId) => {
    const descriptions = {
      starter: 'Perfect for small businesses just starting out',
      basic: 'For small businesses and freelancers',
      pro: 'For agencies and growing companies',
      enterprise: 'For large enterprises with custom needs'};
    return descriptions[planId] || 'Custom pricing plan';
  };
  
  // Fallback default plans
  const getDefaultPlans = () => [
    {
      id: 'starter',
      name: 'Starter ( Free Trial Pack )',
      price: '₹0',
      priceValue: 0,
      priceId: 'price_starter',
      description: 'Perfect for small businesses just starting out',
      features: ['2 job postings', '50 AI credits', 'Basic support', '1 recruiter seat'],
      popular: false,
      isFree: true},
    {
      id: 'pro',
      name: 'Pro',
      price: '₹599',
      priceValue: 599,
      priceId: 'price_pro',
      description: 'For agencies and growing companies',
      features: ['Unlimited job postings', '500 AI credits', 'Priority support', '5 recruiter seats'],
      popular: true},
  ];
  
  // Update current plan from user's subscriptionPlan field
  useEffect(() => {
    if (isAuthenticated && user && user.subscriptionPlan) {
      // User's subscriptionPlan is already the lowercase ID (e.g., "starter", "pro")
      setCurrentPlan(user.subscriptionPlan);
    } else if (isAuthenticated && user && user.userType === 'employer' && !user.subscriptionPlan) {
      // If employer doesn't have a plan set, default to starter
      setCurrentPlan('starter');
    } else if (!isAuthenticated) {
      // Reset current plan when user is not authenticated
      setCurrentPlan(null);
    }
  }, [user, isAuthenticated]);

  // Handle plan selection
  const handleSelectPlan = (planId) => {

    setSelectedPlan(planId);
    setIsProcessing(true);
    
    const plan = pricingPlans.find(p => p.id === planId);

    if (!plan) {
      console.error('[ERROR] Plan not found!');
      setIsProcessing(false);
      return;
    }
    
    setTimeout(() => {
      setIsProcessing(false);

      navigate('/payment', {
        state: {
          paymentDetails: {
            planName: plan.name,
            planPrice: plan.price,
            planDuration: 'month',
            planId: plan.priceId,
            isSubscription: true
          }
        }
      });
    }, 500);
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-100">
      {/* Only show header if user is not logged in */}
      {!isAuthenticated && <Header />}
      <main className="flex-1 flex flex-col">
        <section className="w-full bg-gray-100 py-16 px-4 sm:px-6 lg:px-16 relative overflow-hidden min-h-[calc(100vh-80px)]">
          {/* Subtle background pattern */}
          <div className="absolute inset-0 opacity-[0.03] pointer-events-none">
            <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(#000 1px, transparent 1px)', backgroundSize: '40px 40px' }}></div>
          </div>
          
          {/* Page Title */}
          <div className="text-center mb-16">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-black text-black leading-none"
            >
              PRICI<span className="inline-block relative px-1">
                <span className="relative z-10">N</span>
                <span className="absolute inset-0 bg-yellow-400 rounded-full transform scale-105 -z-0"></span>
              </span>G
            </motion.h1>
            
            <div className="relative mx-auto" style={{ height: '6px', width: '100px', marginTop: '32px', marginBottom: '32px' }}>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3, duration: 0.7 }}
                className="bg-black absolute"
                style={{ height: '6px', width: '100%', left: 0 }}
              ></motion.div>
            </div>
            
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.7 }}
              className="text-xl text-black max-w-2xl mx-auto"
            >
            </motion.p>
          </div>
          
          {/* Pricing Plans */}
          <div className="max-w-7xl mx-auto">
            {loading ? (
              <div className="flex justify-center items-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-black"></div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {pricingPlans.map((plan, index) => (
                <motion.div
                  key={plan.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                >
                  <div 
                    className={`p-6 h-full ${plan.popular ? 'border-2 border-black' : 'border border-gray-200'} rounded-xl transition-all duration-300 hover:shadow-2xl`}
                    style={{
                      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                      background: (isAuthenticated && plan.id === currentPlan) 
                        ? 'linear-gradient(145deg, #fef3c7, #fde68a)' // Yellow gradient for current plan
                        : 'linear-gradient(145deg, #ffffff, #f8f9fa)',
                      position: 'relative'
                    }}
                    data-component-name="PricingPage"
                  >
                    {plan.popular && (
                      <div className="absolute -top-4 right-4">
                        <div className="px-3 py-1 bg-black text-white text-xs font-bold">
                          MOST POPULAR
                        </div>
                      </div>
                    )}
                    {isAuthenticated && plan.id === currentPlan && (
                      <div className="absolute -top-4 left-4">
                        <div className="px-3 py-1 bg-yellow-500 text-black text-xs font-bold">
                          CURRENT PLAN
                        </div>
                      </div>
                    )}
                    
                    <div className="mb-6">
                      <h3 className="text-lg font-bold text-black mb-1 min-h-[3.5rem] flex items-center">
                        {plan.name}
                      </h3>
                      <div className="flex items-end gap-1 mb-2">
                        <span className="text-3xl font-bold text-black">{plan.price}</span>
                        {plan.price !== '₹0' && <span className="text-black mb-1">/month</span>}
                      </div>
                      <p className="text-sm text-black mb-2">
                        {plan.description}
                      </p>
                    </div>

                    <div className="space-y-4 mb-8">
                      {plan.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center gap-2 group">
                          <div className="p-1 border border-black text-black transition-all duration-300 group-hover:bg-black group-hover:text-white transform group-hover:scale-110" style={{ boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                            <FiCheck size={16} />
                          </div>
                          <span className="text-black group-hover:font-medium transition-all duration-300">
                            {feature}
                          </span>
                        </div>
                      ))}
                    </div>

                    <div className="mt-auto">
                      <button
                        type="button"
                        className={`w-full py-3 px-4 ${(isAuthenticated && plan.id === currentPlan) ? 'bg-yellow-500 text-black font-bold' : plan.popular ? 'bg-black text-white' : 'bg-white text-black'} border ${(isAuthenticated && plan.id === currentPlan) ? 'border-black border-2' : 'border-black'} transition-all duration-300 transform hover:scale-105 rounded-lg ${(isProcessing && selectedPlan === plan.id) || (isAuthenticated && plan.id === currentPlan) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                        style={{
                          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                          transform: plan.popular || (isAuthenticated && plan.id === currentPlan) ? 'translateY(-2px)' : 'translateY(0)',
                          pointerEvents: 'auto',
                          zIndex: 10
                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();

                          handleSelectPlan(plan.id);
                        }}
                        disabled={(isProcessing && selectedPlan === plan.id) || (isAuthenticated && plan.id === currentPlan)}
                      >
                        {isProcessing && selectedPlan === plan.id ? (
                          <span className="flex items-center justify-center">
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Processing...
                          </span>
                        ) : (
                          <>
                            {isAuthenticated && plan.id === currentPlan ? 'Current Plan' : 
                             plan.isFree ? 'Get Started Free' :
                             plan.popular ? 'Upgrade to Pro' : 
                             `Get ${plan.name}`}
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}

              {/* Custom Plan Card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: pricingPlans.length * 0.1 }}
              >
                <div 
                  className="p-6 h-full border-2 border-black rounded-xl transition-all duration-300 hover:shadow-2xl cursor-pointer"
                  style={{
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                    background: 'linear-gradient(145deg, #e0e7ff, #c7d2fe)',
                    position: 'relative'
                  }}
                  onClick={() => setIsCustomPlanModalOpen(true)}
                >
                  <div className="mb-6">
                    <h3 className="text-lg font-bold text-black mb-1 min-h-[3.5rem] flex items-center">
                      Enterprise
                    </h3>
                    <div className="flex items-end gap-1 mb-2">
                      <span className="text-3xl font-bold text-black">Custom</span>
                    </div>
                    <p className="text-sm text-black mb-2">
                      Tailored solutions for large organizations
                    </p>
                  </div>

                  <div className="space-y-4 mb-8">
                    <div className="flex items-center gap-2 group">
                      <div className="p-1 border border-black text-black transition-all duration-300 group-hover:bg-black group-hover:text-white transform group-hover:scale-110" style={{ boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                        <FiCheck size={16} />
                      </div>
                      <span className="text-black group-hover:font-medium transition-all duration-300">
                        Unlimited job postings
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-2 group">
                      <div className="p-1 border border-black text-black transition-all duration-300 group-hover:bg-black group-hover:text-white transform group-hover:scale-110" style={{ boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                        <FiCheck size={16} />
                      </div>
                      <span className="text-black group-hover:font-medium transition-all duration-300">
                        Custom AI credits
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-2 group">
                      <div className="p-1 border border-black text-black transition-all duration-300 group-hover:bg-black group-hover:text-white transform group-hover:scale-110" style={{ boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                        <FiCheck size={16} />
                      </div>
                      <span className="text-black group-hover:font-medium transition-all duration-300">
                        Custom recruiter seats
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-2 group">
                      <div className="p-1 border border-black text-black transition-all duration-300 group-hover:bg-black group-hover:text-white transform group-hover:scale-110" style={{ boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                        <FiCheck size={16} />
                      </div>
                      <span className="text-black group-hover:font-medium transition-all duration-300">
                        Priority support
                      </span>
                    </div>
                  </div>

                  <div className="mt-auto">
                    <button
                      type="button"
                      className="w-full py-3 px-4 bg-indigo-600 text-white border border-black transition-all duration-300 transform hover:scale-105 rounded-lg cursor-pointer flex items-center justify-center gap-2"
                      style={{
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                        pointerEvents: 'auto',
                        zIndex: 10
                      }}
                      onClick={() => setIsCustomPlanModalOpen(true)}
                    >
                      <FiMail size={18} />
                      Contact Us
                    </button>
                  </div>
                </div>
              </motion.div>
            </div>
            )}
          </div>
        </section>

        {/* Custom Plan Modal */}
        <CustomPlanModal 
          isOpen={isCustomPlanModalOpen}
          onClose={() => setIsCustomPlanModalOpen(false)} 
        />

        {/* CTA Section - Only show for non-logged in users */}
        {!isAuthenticated && (
          <section className="bg-black p-8 text-white text-center border border-black">
            <h3 className="text-2xl font-bold mb-4">Ready to transform your hiring process?</h3>
            <p className="mb-6 max-w-2xl mx-auto">
              Join thousands of companies using Job Loop to find the best talent faster and more efficiently.
            </p>
            <button
              className="bg-white text-black px-6 py-3 border border-white font-medium flex items-center gap-2 mx-auto hover:bg-gray-100 transition-colors"
              onClick={() => navigate('/auth/employer')}
            >
              Get Started Today
              <FiArrowRight size={16} />
            </button>
          </section>
        )}
      </main>
    </div>
  );
};

export default PricingPage;
